import { Ledger } from "@common/contracts/ledger.contract";
import { ConsolidationGroupResponse } from "@common/schemas/domains/accounting/consolidation.schema";
import { ConsolidationGroupStatus } from "@common/types/domains/accounting/consolidation";
import { Entity } from "@common/types/entity";
import { getConsolidationGroupBasis } from "./panels/consolidationSettings.helpers";

type ConsolidatedReport = "balance-sheet" | "profit-loss";

// The consolidated report renders the parent entity on the group's basis, so
// resolve the parent's primary ledger for that basis (falling back to any
// primary ledger, then any ledger, if none matches exactly).
export function resolveConsolidatedReportLedgerId({
    group,
    ledgers,
}: {
    group: ConsolidationGroupResponse;
    ledgers: Ledger[] | undefined;
}): string | undefined {
    const configuredParentLedgerId =
        group.config?.ledgerIdsByEntityUuid?.[group.parentEntityUuid];
    const configuredLedger = ledgers?.find(
        ({ id }) => id === configuredParentLedgerId,
    );
    if (configuredLedger) {
        return configuredLedger.id;
    }

    const basis = getConsolidationGroupBasis(group);
    return (
        ledgers?.find((ledger) => ledger.isPrimary && ledger.basis === basis)
            ?.id ??
        ledgers?.find((ledger) => ledger.isPrimary)?.id ??
        ledgers?.[0]?.id
    );
}

export function canOpenConsolidatedReport({
    group,
    parentEntity,
    ledgerId,
}: {
    group: ConsolidationGroupResponse;
    parentEntity: Entity | undefined;
    ledgerId: string | undefined;
}): boolean {
    return Boolean(
        group.status === ConsolidationGroupStatus.active &&
        parentEntity &&
        ledgerId,
    );
}

export function buildConsolidatedReportUrl({
    report,
    parentEntity,
    ledgerId,
    group,
    dateRange,
}: {
    report: ConsolidatedReport;
    parentEntity: Entity;
    ledgerId?: string;
    group: ConsolidationGroupResponse;
    dateRange?: { startDate: string; endDate: string };
}): string {
    const params = new URLSearchParams({
        entityId: parentEntity.id.toString(),
    });
    if (ledgerId) {
        params.append("ledgerId", ledgerId);
    }
    params.append("consolidationGroupId", group.id);
    if (dateRange) {
        params.append("startDate", dateRange.startDate);
        params.append("endDate", dateRange.endDate);
    }
    return `/accounting/${report}?${params.toString()}`;
}
